import React, { useState, useMemo, useEffect } from 'react';
import { User } from '../types';
import { PageHeader } from './ui/PageHeader';
import { db } from '../services/db';
import { UpgradeRequired } from './ui/UpgradeRequired';
import { ConfirmModal } from './ui/ConfirmModal';
import {
  Calculator,
  Package,
  HardHat,
  Car,
  Settings2,
  Percent,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  DollarSign,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface MarkupCalculatorProps {
  currentUser: User;
  currentCompany?: any;
  onCreateOrderFromQuote?: (quote: any) => void;
  onChangeView: (view: string) => void;
}

// ── Utilities ────────────────────────────────────────────────────────────────

const parseBRL = (str: string): number => {
  const clean = str.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(clean);
  return isNaN(n) ? 0 : n;
};

const applyMask = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (!digits || parseInt(digits, 10) === 0) return '';
  const number = parseInt(digits, 10);
  return (number / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const brlDisplay = (value: number): string =>
  value === 0 ? '' : value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const displayBRL = (value: number): string =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtResult = (value: number): string =>
  'R$ ' + value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt = fmtResult;

// ── Sub-components ────────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Section: React.FC<SectionProps> = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        background: 'rgba(255,255,255,.03)',
        border: '1px solid rgba(255,255,255,.08)',
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          background: 'rgba(255,255,255,.02)',
          border: 'none',
          cursor: 'pointer',
          borderBottom: open ? '1px solid rgba(255,255,255,.06)' : 'none',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 14, color: 'var(--sf-white)' }}>
          {icon}
          {title}
        </span>
        {open ? <ChevronUp size={16} style={{ color: 'var(--muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--muted)' }} />}
      </button>
      {open && <div style={{ padding: '18px 20px' }}>{children}</div>}
    </div>
  );
};

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, hint, children }) => (
  <div className="space-y-1">
    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block' }}>
      {label}
    </label>
    {children}
    {hint && (
      <p style={{ fontSize: 11, color: 'var(--hint)', marginTop: 3 }}>{hint}</p>
    )}
  </div>
);

interface ResultCardProps {
  label: string;
  value: string;
  accent?: string;
  large?: boolean;
}

const ResultCard: React.FC<ResultCardProps> = ({ label, value, accent = 'var(--cyan)', large }) => (
  <div style={{
    background: 'rgba(255,255,255,.04)',
    border: `1px solid ${accent}33`,
    borderRadius: 14,
    padding: large ? '18px 20px' : '14px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  }}>
    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</span>
    <span style={{ fontSize: large ? 26 : 20, fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums' }}>
      R$ {value}
    </span>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────

export const MarkupCalculator: React.FC<MarkupCalculatorProps> = ({ currentUser, currentCompany, onCreateOrderFromQuote, onChangeView }) => {

  // Produto
  const [fabrica, setFabrica] = useState(0);
  const [freteProd, setFreteProd] = useState(0);

  // Mão de obra
  const [nmont, setNmont] = useState(1);
  const [dmont, setDmont] = useState(0);
  const [dias, setDias] = useState(1);
  const [naux, setNaux] = useState(0);
  const [daux, setDaux] = useState(0);

  // Deslocamento
  const [frete, setFrete] = useState(0);
  const [pedagio, setPedagio] = useState(0);

  // Operacional
  const [mat, setMat] = useState(0);
  const [alim, setAlim] = useState(0);
  const [fixo, setFixo] = useState(0);
  const [outros, setOutros] = useState(0);

  // Markup / impostos
  const [markup, setMarkup] = useState(40);
  const [imp, setImp] = useState(6);
  const [com, setCom] = useState(0);
  const [comArquiteto, setComArquiteto] = useState(0);

  // Preço fechado
  const [fechado, setFechado] = useState(0);
  const [desconto, setDesconto] = useState<number>(0);

  // ── Cálculos ──────────────────────────────────────────────────────────────

  const calc = useMemo(() => {
    const cProd = fabrica + freteProd;
    const cMO = (nmont * dmont + naux * daux) * dias;
    const cDesl = frete + pedagio;
    const cOp = mat + alim * dias + fixo + outros;
    const custo = cProd + cMO + cDesl + cOp;
    const ded = (imp + com + comArquiteto) / 100;
    const precoMin = ded < 1 ? custo / (1 - ded) : custo;
    const precoSug = ded < 1 ? (custo * (1 + markup / 100)) / (1 - ded) : custo * (1 + markup / 100);
    const lucro = precoSug - custo - precoSug * ded;
    const margPct = precoSug > 0 ? (lucro / precoSug) * 100 : 0;

    const mReal = fechado - custo - fechado * ded;
    const mRealPct = fechado > 0 ? (mReal / fechado) * 100 : 0;

    return { cProd, cMO, cDesl, cOp, custo, precoMin, precoSug, lucro, margPct, mReal, mRealPct };
  }, [fabrica, freteProd, nmont, dmont, dias, naux, daux, frete, pedagio, mat, alim, fixo, outros, markup, imp, com, comArquiteto, fechado]);

  const precoBase = desconto > 0 ? calc.precoSug * (1 - desconto / 100) : calc.precoSug;
  const comissaoVendedorValor = precoBase * (com / 100);
  const comissaoArquitetoValor = precoBase * (comArquiteto / 100);
  const custoTotalComComissoes = calc.custo + comissaoVendedorValor + comissaoArquitetoValor;

  const lucroEstimado = precoBase - custoTotalComComissoes - (precoBase * (imp / 100));

  const margemLiquida = precoBase > 0
    ? (lucroEstimado / precoBase) * 100
    : 0;

  const precoComDesconto = calc.precoSug * (1 - desconto / 100);

  // Histórico e modal
  const isPro = ['pro', 'business'].includes(currentCompany?.plano?.toLowerCase());
  const [quotes, setQuotes] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'calc' | 'hist'>('calc');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showUpgradeLockModal, setShowUpgradeLockModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [saveCliente, setSaveCliente] = useState('');
  const [saveDescricao, setSaveDescricao] = useState('');
  const [saveAmbientes, setSaveAmbientes] = useState<string[]>(['']);
  const [saveStatus, setSaveStatus] = useState('rascunho');
  const [loadedQuoteId, setLoadedQuoteId] = useState<string | null>(null);
  const [loadedQuoteCliente, setLoadedQuoteCliente] = useState<string>('');
  const [loadedQuoteAmbientes, setLoadedQuoteAmbientes] = useState<string[]>([]);
  const [loadedQuoteStatus, setLoadedQuoteStatus] = useState<string>('rascunho');
  const [expandedQuoteId, setExpandedQuoteId] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [editingAmbientesId, setEditingAmbientesId] = useState<string | null>(null);
  const [editingAmbientes, setEditingAmbientes] = useState<string[]>([]);

  const loadQuotes = async () => {
    setLoadingQuotes(true);
    const data = await db.getMarkupQuotes();
    setQuotes(data);
    setLoadingQuotes(false);
  };

  useEffect(() => {
    if (currentUser.role !== 'ADMIN') return;
    const plan = currentCompany?.plano?.toLowerCase();
    if (!['pro', 'business'].includes(plan)) return;
    loadQuotes();
  }, [currentUser, currentCompany]);

  const handleSaveQuote = async () => {
    if (!saveCliente.trim()) return;
    const quote = {
      cliente: saveCliente.trim(),
      descricao: saveAmbientes.filter(a => a.trim()).join(' | '),
      status: saveStatus,
      fabrica: fabrica,
      frete_prod: freteProd,
      n_montadores: nmont,
      diaria_montador: dmont,
      n_auxiliares: naux,
      diaria_auxiliar: daux,
      dias_servico: dias,
      frete_equipe: frete,
      pedagio: pedagio,
      materiais: mat,
      alimentacao_dia: alim,
      custo_fixo: fixo,
      outros: outros,
      markup_pct: markup,
      impostos_pct: imp,
      comissao_vendedor_pct: com,
      comissao_arquiteto_pct: comArquiteto,
      preco_fechado: fechado,
      custo_total: calc.custo,
      preco_minimo: calc.precoMin,
      preco_sugerido: calc.precoSug,
      margem_pct: margemLiquida,
      desconto_pct: desconto,
      preco_com_desconto: desconto > 0 ? calc.precoSug * (1 - desconto / 100) : null,
      valor_desconto: desconto > 0 ? calc.precoSug * (desconto / 100) : null,
      comissao_vendedor_valor: comissaoVendedorValor,
      comissao_arquiteto_valor: comissaoArquitetoValor,
      lucro_estimado: lucroEstimado,
    };
    const saved = await db.saveMarkupQuote(quote);
    if (saved) {
      setQuotes(prev => [saved, ...prev]);
      setShowSaveModal(false);
      setSaveCliente('');
      setSaveDescricao('');
      setSaveAmbientes(['']);
      setSaveStatus('rascunho');
    }
  };

  const handleLoadQuote = (q: any) => {
    setFabrica(q.fabrica || 0);
    setFreteProd(q.frete_prod || 0);
    setNmont(q.n_montadores || 0);
    setDmont(q.diaria_montador || 0);
    setNaux(q.n_auxiliares || 0);
    setDaux(q.diaria_auxiliar || 0);
    setDias(q.dias_servico || 1);
    setFrete(q.frete_equipe || 0);
    setPedagio(q.pedagio || 0);
    setMat(q.materiais || 0);
    setAlim(q.alimentacao_dia || 0);
    setFixo(q.custo_fixo || 0);
    setOutros(q.outros || 0);
    setMarkup(q.markup_pct || 0);
    setImp(q.impostos_pct || 0);
    setCom(q.comissao_vendedor_pct || 0);
    setComArquiteto(q.comissao_arquiteto_pct || 0);
    setFechado(q.preco_fechado || 0);

    // Guardar referência ao orçamento carregado
    setLoadedQuoteId(q.id);
    setLoadedQuoteCliente(q.cliente);
    setLoadedQuoteAmbientes(q.descricao ? q.descricao.split(' | ') : ['']);
    setLoadedQuoteStatus(q.status);
    setActiveTab('calc');
  };

  const handleUpdateLoadedQuote = async () => {
    if (!loadedQuoteId) return;

    const updates = {
      fabrica: fabrica,
      frete_prod: freteProd,
      n_montadores: nmont,
      diaria_montador: dmont,
      n_auxiliares: naux,
      diaria_auxiliar: daux,
      dias_servico: dias,
      frete_equipe: frete,
      pedagio: pedagio,
      materiais: mat,
      alimentacao_dia: alim,
      custo_fixo: fixo,
      outros: outros,
      markup_pct: markup,
      impostos_pct: imp,
      comissao_vendedor_pct: com,
      comissao_arquiteto_pct: comArquiteto,
      preco_fechado: fechado,
      custo_total: calc.custo,
      preco_minimo: calc.precoMin,
      preco_sugerido: calc.precoSug,
      margem_pct: margemLiquida,
      descricao: loadedQuoteAmbientes.filter(a => a.trim()).join(' | '),
      status: loadedQuoteStatus,
      desconto_pct: desconto,
      preco_com_desconto: desconto > 0 ? calc.precoSug * (1 - desconto / 100) : null,
      valor_desconto: desconto > 0 ? calc.precoSug * (desconto / 100) : null,
      comissao_vendedor_valor: comissaoVendedorValor,
      comissao_arquiteto_valor: comissaoArquitetoValor,
      lucro_estimado: lucroEstimado,
    };

    await db.updateMarkupQuote(loadedQuoteId, updates);
    setQuotes(prev => prev.map(q => q.id === loadedQuoteId ? { ...q, ...updates } : q));

    // Limpar o estado de edição
    setLoadedQuoteId(null);
    setLoadedQuoteCliente('');
    setLoadedQuoteAmbientes(['']);
    setLoadedQuoteStatus('rascunho');
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    await db.updateMarkupQuote(id, { status });
    setQuotes(prev => prev.map(q => q.id === id ? { ...q, status } : q));
  };

  const handleDeleteQuote = async (id: string) => {
    setConfirmDelete(id);
  };

  // ── Barra de progresso ─────────────────────────────────────────────────────

  const barColor =
    calc.mReal < 0
      ? 'var(--sf-red)'
      : calc.mRealPct < 10
      ? '#f59e0b'
      : '#22c55e';

  const barPct = fechado > 0 ? Math.min(Math.max((calc.mReal / fechado) * 100, -100), 100) : 0;

  // ── Alert contextual ──────────────────────────────────────────────────────

  const Alert = () => {
    if (fechado <= 0) return null;
    if (fechado < calc.precoMin) {
      const prejuizo = calc.precoMin - fechado;
      return (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px',
          background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 12,
        }}>
          <XCircle size={18} style={{ color: 'var(--sf-red)', flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: '#fca5a5', lineHeight: 1.5 }}>
            <strong>Preço abaixo do ponto de equilíbrio.</strong> Prejuízo de <strong>{fmtResult(prejuizo)}</strong>.
          </p>
        </div>
      );
    }
    if (fechado < calc.precoSug) {
      return (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px',
          background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 12,
        }}>
          <AlertTriangle size={18} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: '#fcd34d', lineHeight: 1.5 }}>
            Preço cobre os custos mas está <strong>abaixo do markup desejado</strong>.
          </p>
        </div>
      );
    }
    return (
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px',
        background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 12,
      }}>
        <CheckCircle size={18} style={{ color: '#22c55e', flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 13, color: '#86efac', lineHeight: 1.5 }}>
          Dentro do markup desejado. <strong>Lucro líquido de {fmtResult(calc.mReal)}</strong>.
        </p>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────



  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Calculadora de Markup"
        description="Calcule o preço sugerido e a margem líquida com base nos seus custos reais."
        icon={<Calculator size={22} style={{ color: 'var(--cyan)' }} />}
      />

      {/* Tabs */}
      <div style={{ display:'flex', gap:2, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, padding:3, width:'fit-content', marginBottom:20 }}>
        <button
          onClick={() => setActiveTab('calc')}
          style={{ padding:'7px 18px', borderRadius:5, fontSize:13, fontWeight:500, cursor:'pointer', border: activeTab==='calc' ? '1px solid var(--border)' : 'none', background: activeTab==='calc' ? 'var(--bg2)' : 'transparent', color: activeTab==='calc' ? 'var(--text)' : 'var(--muted)' }}
        >
          Calculadora
        </button>
        <button
          onClick={() => { setActiveTab('hist'); if (isPro) loadQuotes(); }}
          style={{ padding:'7px 18px', borderRadius:5, fontSize:13, fontWeight:500, cursor:'pointer', border: activeTab==='hist' ? '1px solid var(--border)' : 'none', background: activeTab==='hist' ? 'var(--bg2)' : 'transparent', color: activeTab==='hist' ? 'var(--text)' : 'var(--muted)', display:'flex', alignItems:'center', gap:7 }}
        >
          Histórico de Orçamentos
          {isPro && quotes.length > 0 && (
            <span style={{ background:'var(--blue-dim)', color:'var(--blue)', borderRadius:20, fontSize:10, fontWeight:700, padding:'1px 6px' }}>{quotes.length}</span>
          )}
        </button>
      </div>

      {activeTab === 'calc' && (
        <>
          {loadedQuoteId && (
            <div style={{
              background: 'rgba(56,139,253,.1)',
              border: '1px solid rgba(56,139,253,.3)',
              borderRadius: 10,
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--blue)' }}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span>Editando orçamento de <strong>{loadedQuoteCliente}</strong></span>
                </div>
                <button
                  onClick={() => {
                    setLoadedQuoteId(null);
                    setLoadedQuoteCliente('');
                    setLoadedQuoteAmbientes(['']);
                    setLoadedQuoteStatus('rascunho');
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(56,139,253,.3)',
                    borderRadius: 6,
                    padding: '4px 10px',
                    fontSize: 12,
                    color: 'var(--blue)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Descartar edição
                </button>
              </div>

              {/* Ambientes editáveis */}
              <div style={{ marginTop: 10, borderTop: '1px solid rgba(56,139,253,.2)', paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--blue)', marginBottom: 6, fontWeight: 600 }}>
                  Ambientes do projeto
                </div>
                {loadedQuoteAmbientes.map((amb, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <input
                      type="text"
                      value={amb}
                      onChange={e => {
                        const next = [...loadedQuoteAmbientes];
                        next[idx] = e.target.value;
                        setLoadedQuoteAmbientes(next);
                      }}
                      placeholder={idx === 0 ? 'Ex: Cozinha' : 'Ex: Sala de estar'}
                      style={{
                        flex: 1,
                        background: 'rgba(56,139,253,.08)',
                        border: '1px solid rgba(56,139,253,.25)',
                        borderRadius: 6,
                        color: 'var(--text)',
                        fontFamily: 'Inter,sans-serif',
                        fontSize: 12,
                        padding: '5px 8px',
                        outline: 'none',
                      }}
                    />
                    {loadedQuoteAmbientes.length > 1 && (
                      <button
                        onClick={() => setLoadedQuoteAmbientes(loadedQuoteAmbientes.filter((_, i) => i !== idx))}
                        style={{ background: 'transparent', border: 'none', color: 'rgba(56,139,253,.6)', cursor: 'pointer', fontSize: 14, padding: '2px 5px' }}
                      >✕</button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setLoadedQuoteAmbientes([...loadedQuoteAmbientes, ''])}
                  style={{ fontSize: 11, color: 'var(--blue)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 0', marginTop: 2 }}
                >
                  ＋ Adicionar ambiente
                </button>
              </div>

              {/* Status */}
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--blue)' }}>Status:</span>
                <select
                  value={loadedQuoteStatus}
                  onChange={e => setLoadedQuoteStatus(e.target.value)}
                  style={{
                    background: 'rgba(56,139,253,.08)',
                    border: '1px solid rgba(56,139,253,.25)',
                    borderRadius: 6,
                    color: 'var(--text)',
                    fontFamily: 'Inter,sans-serif',
                    fontSize: 12,
                    padding: '4px 8px',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="rascunho">Rascunho</option>
                  <option value="enviado">Enviado</option>
                  <option value="aprovado">Aprovado</option>
                  <option value="recusado">Recusado</option>
                </select>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6 items-start">

          {/* ── Coluna esquerda: entradas ─── */}
          <div className="space-y-4">

            {/* Produto */}
            <Section title="Produto" icon={<Package size={16} style={{ color: 'var(--blue)' }} />}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Preço de fábrica (R$)" hint="Custo junto ao fabricante">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(fabrica)}
                    onChange={e => setFabrica(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
                <Field label="Frete do produto (R$)" hint="Frete da fábrica até a obra/cliente">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(freteProd)}
                    onChange={e => setFreteProd(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
              </div>
            </Section>

            {/* Mão de obra */}
            <Section title="Mão de obra" icon={<HardHat size={16} style={{ color: '#a78bfa' }} />}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Nº de técnicos">
                  <input
                    type="number"
                    min={0}
                    className="input-field"
                    placeholder="0"
                    value={nmont || ''}
                    onChange={e => setNmont(Number(e.target.value))}
                  />
                </Field>
                <Field label="Diária do técnico (R$)">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(dmont)}
                    onChange={e => setDmont(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
                <Field label="Nº de dias de serviço">
                  <input
                    type="number"
                    min={1}
                    className="input-field"
                    placeholder="1"
                    value={dias || ''}
                    onChange={e => setDias(Number(e.target.value))}
                  />
                </Field>
                <Field label="Nº de auxiliares">
                  <input
                    type="number"
                    min={0}
                    className="input-field"
                    placeholder="0"
                    value={naux || ''}
                    onChange={e => setNaux(Number(e.target.value))}
                  />
                </Field>
                <Field label="Diária do auxiliar (R$)">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(daux)}
                    onChange={e => setDaux(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
              </div>
            </Section>

            {/* Deslocamento */}
            <Section title="Deslocamento da equipe" icon={<Car size={16} style={{ color: '#34d399' }} />}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Combustível / translado (R$)">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(frete)}
                    onChange={e => setFrete(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
                <Field label="Pedágio (R$)">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(pedagio)}
                    onChange={e => setPedagio(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
              </div>
            </Section>

            {/* Custos operacionais */}
            <Section title="Custos operacionais" icon={<Settings2 size={16} style={{ color: '#fb923c' }} />}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Materiais de consumo (R$)">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(mat)}
                    onChange={e => setMat(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
                <Field label="Alimentação por dia (R$)" hint="Custo diário × número de dias">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(alim)}
                    onChange={e => setAlim(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
                <Field label="Rateio custo fixo (R$)" hint="Aluguel, contador, ferramentas...">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(fixo)}
                    onChange={e => setFixo(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
                <Field label="Outros (R$)">
                  <input
                    type="text"
                    className="input-field"
                    inputMode="decimal"
                    placeholder="0"
                    value={brlDisplay(outros)}
                    onChange={e => setOutros(parseBRL(applyMask(e.target.value)))}
                  />
                </Field>
              </div>
            </Section>

            {/* Markup e impostos */}
            <Section title="Markup e impostos" icon={<Percent size={16} style={{ color: 'var(--cyan)' }} />}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Markup desejado (%)">
                  <input
                    type="number"
                    min={0}
                    className="input-field"
                    placeholder="40"
                    value={markup || ''}
                    onChange={e => setMarkup(Number(e.target.value))}
                  />
                </Field>
                <Field label="Impostos (%)">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="input-field"
                    placeholder="6"
                    value={imp || ''}
                    onChange={e => setImp(Number(e.target.value))}
                  />
                </Field>
                <Field label="Comissão vendedor (%)">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="input-field"
                    placeholder="0"
                    value={com || ''}
                    onChange={e => setCom(Number(e.target.value))}
                  />
                </Field>
                <Field label="Comissão do arquiteto (%)">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="input-field"
                    placeholder="0"
                    value={comArquiteto || ''}
                    onChange={e => setComArquiteto(Number(e.target.value))}
                  />
                </Field>
              </div>
            </Section>

          </div>


          {/* ── Coluna direita: resultados ─── */}
          <div className="space-y-4">

            {/* Breakdown de custos */}
            <div style={{
              background: 'rgba(255,255,255,.03)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 16,
              padding: '18px 20px',
            }}>
              <p style={{ fontWeight: 700, fontSize: 14, color: 'var(--sf-white)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <DollarSign size={16} style={{ color: 'var(--blue)' }} /> Breakdown de Custos
              </p>
              <div className="space-y-2">
                {[
                  { label: 'Produto (fábrica + frete)', value: calc.cProd, color: 'var(--blue)' },
                  { label: 'Mão de obra', value: calc.cMO, color: '#a78bfa' },
                  { label: 'Deslocamento', value: calc.cDesl, color: '#34d399' },
                  { label: 'Operacional', value: calc.cOp, color: '#fb923c' },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderRadius: 8, background: 'rgba(255,255,255,.02)' }}>
                    <span style={{ fontSize: 13, color: 'var(--muted)' }}>{row.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: row.color, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtResult(row.value)}
                    </span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', marginTop: 6, paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--sf-white)' }}>Custo total</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--sf-white)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtResult(calc.custo)}
                  </span>
                </div>
              </div>
            </div>

            {/* Quadro de comissões — espelhado na coluna de resultados */}
            {(com > 0 || comArquiteto > 0 || imp > 0) && (
              <div
                className="animate-fade-in"
                style={{
                  background: 'rgba(255,255,255,.03)',
                  border: '1px solid rgba(255,255,255,.08)',
                  borderRadius: 16,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Percent size={14} style={{ color: 'var(--cyan)' }} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sf-white)' }}>Comissões sobre o preço sugerido</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                      {['Comissão', '%', 'Valor (R$)'].map(h => (
                        <th key={h} style={{ padding: '8px 16px', textAlign: h === 'Comissão' ? 'left' : 'right', fontSize: 10, fontWeight: 700, color: 'var(--hint)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Vendedor',   pct: com,           value: (com / 100) * precoBase },
                      { label: 'Arquiteto',  pct: comArquiteto,  value: (comArquiteto / 100) * precoBase },
                      { label: 'Impostos',   pct: imp,           value: (imp / 100) * precoBase },
                    ].map(row => (
                      <tr key={row.label} style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                        <td style={{ padding: '8px 16px', fontSize: 13, color: 'var(--muted)' }}>{row.label}</td>
                        <td style={{ padding: '8px 16px', fontSize: 13, color: 'var(--muted)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.pct}%</td>
                        <td style={{ padding: '8px 16px', fontSize: 13, color: 'var(--sf-white)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtResult(row.value)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '1px solid rgba(255,255,255,.1)' }}>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, color: 'var(--blue)' }}>Total deduzido</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 500, color: 'var(--blue)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{imp + com + comArquiteto}%</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, color: 'var(--blue)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtResult(((imp + com + comArquiteto) / 100) * precoBase)}</td>
                    </tr>
                  </tbody>
                </table>
                {desconto > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--amber)', padding: '6px 16px', borderTop: '1px solid var(--border)' }}>
                    ⚠ Comissões recalculadas sobre o preço com desconto de {desconto}%
                  </div>
                )}
              </div>
            )}

            {/* Card de resumo de custo total (Custos + Comissões) */}
            <div style={{
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              margin: '4px 0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="14" height="14" fill="none" stroke="var(--muted)" strokeWidth="2" viewBox="0 0 24 24">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  Custo total (operacional + comissões)
                </span>
              </div>
              <span style={{
                fontFamily: 'DM Mono, monospace',
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--red)'
              }}>
                {fmtResult(custoTotalComComissoes)}
              </span>
            </div>

            {/* Cards de resultado */}
            <div style={{
              background: 'rgba(255,255,255,.03)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 16,
              padding: '18px 20px',
            }}>
              <p style={{ fontWeight: 700, fontSize: 14, color: 'var(--sf-white)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={16} style={{ color: 'var(--cyan)' }} /> Resultado Sugerido
              </p>
              
              <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 12 }}>
                <ResultCard label="Preço mínimo (break-even)" value={displayBRL(calc.precoMin)} accent="#f59e0b" />
                <ResultCard label="Preço sugerido" value={displayBRL(calc.precoSug)} accent="var(--cyan)" large />
              </div>

              {/* Campo de desconto */}
              <div style={{
                borderTop: '1px solid var(--border)',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
                <span style={{ fontSize: 13, color: 'var(--muted)', flex: 1 }}>
                  Aplicar desconto
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={desconto || ''}
                    onChange={e => setDesconto(Math.min(50, Math.max(0, parseFloat(e.target.value) || 0)))}
                    placeholder="0"
                    style={{
                      width: 70,
                      background: 'var(--bg3)',
                      border: '1px solid var(--border)',
                      borderRadius: 7,
                      color: 'var(--text)',
                      fontFamily: 'DM Mono, monospace',
                      fontSize: 13,
                      padding: '6px 10px',
                      outline: 'none',
                      textAlign: 'right',
                    }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>%</span>
                </div>
              </div>

              {desconto > 0 && (
                <div style={{
                  borderTop: '1px solid var(--border)',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
                      Preço com desconto de {desconto}%
                    </div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 20, fontWeight: 700, color: 'var(--amber)' }}>
                      {fmtResult(precoComDesconto)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 3, fontFamily: 'DM Mono, monospace' }}>
                      − {fmtResult(calc.precoSug - precoComDesconto)} de desconto
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
                      Margem com desconto
                    </div>
                    <div style={{
                      fontFamily: 'DM Mono, monospace',
                      fontSize: 20,
                      fontWeight: 700,
                      color: margemLiquida > 0 ? 'var(--green)' : 'var(--red)'
                    }}>
                      {margemLiquida.toFixed(1).replace('.', ',')}%
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <ResultCard label="Margem líquida" value={`${displayBRL(margemLiquida)}%`} accent="#a78bfa" />
                <ResultCard label="Lucro estimado" value={displayBRL(lucroEstimado)} accent="#22c55e" />
              </div>
            </div>

            {/* Preço fechado */}
            <div style={{
              background: 'rgba(255,255,255,.03)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 16,
              padding: '18px 20px',
            }}>
              <p style={{ fontWeight: 700, fontSize: 14, color: 'var(--sf-white)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle size={16} style={{ color: '#22c55e' }} /> Simulação: Preço Fechado
              </p>

              <Field label="Preço fechado com o cliente (R$)">
                <input
                  type="text"
                  className="input-field"
                  inputMode="decimal"
                  placeholder="0"
                  value={brlDisplay(fechado)}
                  onChange={e => setFechado(parseBRL(applyMask(e.target.value)))}
                />
              </Field>

              {fechado > 0 && (
                <div className="space-y-3 animate-fade-in" style={{ marginTop: 16 }}>
                  {/* Barra de progresso */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Margem real</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>
                        {displayBRL(calc.mRealPct)}%
                      </span>
                    </div>
                    <div style={{
                      height: 10, borderRadius: 99, background: 'rgba(255,255,255,.06)', overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.abs(barPct)}%`,
                        background: barColor,
                        borderRadius: 99,
                        transition: 'width .4s ease, background .3s ease',
                        boxShadow: `0 0 10px ${barColor}88`,
                      }} />
                    </div>
                  </div>

                  {/* Alerta contextual */}
                  <Alert />

                  {/* Lucro / prejuízo real */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 14px', borderRadius: 10,
                    background: calc.mReal >= 0 ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)',
                    border: `1px solid ${calc.mReal >= 0 ? 'rgba(34,197,94,.2)' : 'rgba(239,68,68,.2)'}`,
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                      {calc.mReal >= 0 ? 'Lucro líquido' : 'Prejuízo líquido'}
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: calc.mReal >= 0 ? '#22c55e' : 'var(--sf-red)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtResult(Math.abs(calc.mReal))}
                    </span>
                  </div>
                </div>
              )}

              <div style={{ borderTop:'1px solid rgba(255,255,255,.08)', paddingTop:'14px', marginTop:'14px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:12, color:'var(--muted)' }}>💡 Salve como orçamento para acessar depois</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {isPro && loadedQuoteId && (
                    <button
                      onClick={handleUpdateLoadedQuote}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 16px', borderRadius: 7, fontSize: 13, fontWeight: 500,
                        cursor: 'pointer', border: 'none',
                        background: 'var(--green)', color: '#fff',
                      }}
                    >
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Atualizar orçamento
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (!isPro) {
                        setShowUpgradeLockModal(true);
                        return;
                      }
                      setShowSaveModal(true);
                    }}
                    style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:7, fontSize:13, fontWeight:500, cursor:'pointer', border:'none', background:'var(--blue)', color:'#fff' }}
                  >
                    Salvar orçamento
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </>
    )}

      {activeTab === 'hist' && (
        <div style={{ maxWidth: 1000 }}>
          {!isPro ? (
            <UpgradeRequired
              featureName="Histórico de Orçamentos"
              requiredPlan="Pro"
              onUpgrade={() => onChangeView('UPGRADE')}
            />
          ) : (
            <>
              {/* Toolbar */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                  <input
                    type="text"
                    placeholder="Buscar por cliente ou descrição..."
                    value={filterSearch}
                    onChange={e => setFilterSearch(e.target.value)}
                    style={{ width: '100%', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)', fontFamily: 'Inter,sans-serif', fontSize: 13, padding: '8px 10px 8px 34px', outline: 'none' }}
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)', fontFamily: 'Inter,sans-serif', fontSize: 13, padding: '8px 10px', outline: 'none', cursor: 'pointer' }}
                >
                  <option value="">Todos os status</option>
                  <option value="rascunho">Rascunho</option>
                  <option value="enviado">Enviado</option>
                  <option value="aprovado">Aprovado</option>
                  <option value="recusado">Recusado</option>
                </select>
              </div>

              {/* Lista de orçamentos */}
              {loadingQuotes ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Carregando...</div>
              ) : quotes
                .filter(q => {
                  const matchSearch = !filterSearch || q.cliente.toLowerCase().includes(filterSearch.toLowerCase()) || (q.descricao || '').toLowerCase().includes(filterSearch.toLowerCase());
                  const matchStatus = !filterStatus || q.status === filterStatus;
                  return matchSearch && matchStatus;
                })
                .map(q => {
                  const isExpanded = expandedQuoteId === q.id;
                  const statusColors: Record<string, { bg: string; border: string; color: string; label: string }> = {
                    rascunho: { bg: 'var(--bg4)', border: 'var(--border2)', color: 'var(--muted)', label: '◦ Rascunho' },
                    enviado: { bg: 'var(--blue-dim)', border: 'var(--blue-border)', color: 'var(--blue)', label: '↗ Enviado' },
                    aprovado: { bg: 'var(--green-dim)', border: 'var(--green-border)', color: 'var(--green)', label: '✓ Aprovado' },
                    recusado: { bg: 'var(--red-dim)', border: 'var(--red-border)', color: 'var(--red)', label: '✕ Recusado' },
                  };
                  const sc = statusColors[q.status] || statusColors.rascunho;
                  const lucroEst = (q.preco_sugerido || 0) - (q.custo_total || 0) -
                    ((q.preco_sugerido || 0) * (q.impostos_pct || 0) / 100) -
                    ((q.preco_sugerido || 0) * (q.comissao_vendedor_pct || 0) / 100) -
                    ((q.preco_sugerido || 0) * (q.comissao_arquiteto_pct || 0) / 100);
                  return (
                    <div
                      key={q.id}
                      onClick={() => setExpandedQuoteId(isExpanded ? null : q.id)}
                      style={{ background: 'var(--bg2)', border: `1px solid ${isExpanded ? 'var(--blue-border)' : 'var(--border)'}`, borderRadius: 10, overflow: 'hidden', marginBottom: 10, cursor: 'pointer', transition: 'border-color .2s' }}
                    >
                      {/* Header do card */}
                      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--blue-dim)', border: '1px solid var(--blue-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--blue)', flexShrink: 0, fontSize: 13, fontWeight: 700 }}>
                          {q.cliente.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {q.cliente}
                            <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color }}>{sc.label}</span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <span>{new Date(q.created_at).toLocaleDateString('pt-BR')}</span>
                            <span>Markup: {q.markup_pct}% · Impostos: {q.impostos_pct}%</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                          {[
                            { label: 'Custo', val: `R$ ${Math.round(q.custo_total).toLocaleString('pt-BR')}`, color: 'var(--text)' },
                            { label: 'Preço sugerido', val: `R$ ${Math.round(q.preco_sugerido).toLocaleString('pt-BR')}`, color: 'var(--cyan)' },
                            ...(q.desconto_pct > 0 ? [
                              { label: `Com desconto (${q.desconto_pct}%)`, val: fmt(q.preco_com_desconto || 0), color: 'var(--amber)' }
                            ] : []),
                            { label: 'Margem', val: `${(q.margem_pct || 0).toFixed(1).replace('.', ',')}%`, color: 'var(--green)' },
                            { label: 'Lucro estimado', val: fmt(q.lucro_estimado !== null && q.lucro_estimado !== undefined ? q.lucro_estimado : lucroEst), color: 'var(--green)' },
                          ].map(m => (
                            <div key={m.label} style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 10, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{m.label}</div>
                              <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 15, fontWeight: 600, color: m.color }}>{m.val}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => handleLoadQuote(q)} title="Carregar na calculadora"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '6px', borderRadius: 7 }}>
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.2" /></svg>
                          </button>
                          <button onClick={() => handleDeleteQuote(q.id)} title="Excluir"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '6px', borderRadius: 7 }}>
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                          </button>
                        </div>
                      </div>

                      {/* Detalhe expandido */}
                      {isExpanded && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '14px 16px' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 12 }}>
                            {[
                              { label: 'Preço de fábrica', val: `R$ ${q.fabrica?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
                              { label: 'Frete produto', val: `R$ ${q.frete_prod?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
                              {
                                label: 'Mão de obra',
                                val: `R$ ${(((q.n_montadores || 0) * (q.diaria_montador || 0) + (q.n_auxiliares || 0) * (q.diaria_auxiliar || 0)) * (q.dias_servico || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                                sub: (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <span>{q.n_montadores} mont. × R$ {q.diaria_montador?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} × {q.dias_servico}d</span>
                                    {q.n_auxiliares > 0 && (
                                      <span>{q.n_auxiliares} aux. × R$ {q.diaria_auxiliar?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} × {q.dias_servico}d</span>
                                    )}
                                  </div>
                                )
                              },
                              {
                                label: 'Impostos',
                                val: `R$ ${((q.preco_sugerido || 0) * ((q.impostos_pct || 0) / 100)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                              },
                              { label: 'Deslocamento', val: `R$ ${(q.frete_equipe + q.pedagio)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
                              { label: 'Operacional', val: `R$ ${(q.materiais + q.alimentacao_dia * q.dias_servico + q.custo_fixo + q.outros)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
                              {
                                label: 'Comissão vendedor',
                                val: fmt(q.comissao_vendedor_valor !== null && q.comissao_vendedor_valor !== undefined
                                  ? q.comissao_vendedor_valor
                                  : ((q.preco_sugerido || 0) * ((q.comissao_vendedor_pct || 0) / 100)))
                              },
                              {
                                label: 'Comissão arquiteto',
                                val: fmt(q.comissao_arquiteto_valor !== null && q.comissao_arquiteto_valor !== undefined
                                  ? q.comissao_arquiteto_valor
                                  : ((q.preco_sugerido || 0) * ((q.comissao_arquiteto_pct || 0) / 100)))
                              },
                              {
                                label: 'Lucro estimado',
                                val: fmt(q.lucro_estimado !== null && q.lucro_estimado !== undefined
                                  ? q.lucro_estimado
                                  : lucroEst)
                              },
                            ].map((d: { label: string; val: string; sub?: React.ReactNode }) => (
                              <div key={d.label} style={{ background: 'var(--bg3)', borderRadius: 7, padding: '10px 12px' }}>
                                <div style={{ fontSize: 10, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>{d.label}</div>
                                <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{d.val}</div>
                                {d.sub && (
                                  <div style={{ fontSize: 10, color: 'var(--muted2)', marginTop: 4, lineHeight: 1.3 }}>{d.sub}</div>
                                )}
                              </div>
                            ))}
                            {q.desconto_pct > 0 && (
                              <div style={{ background:'var(--bg3)', borderRadius:7, padding:'10px 12px', gridColumn: 'span 2' }}>
                                <div style={{ fontSize:10, color:'var(--muted2)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:3 }}>
                                  Preço com desconto de {q.desconto_pct}%
                                </div>
                                <div style={{ fontFamily:'DM Mono,monospace', fontSize:15, fontWeight:600, color:'var(--amber)' }}>
                                  {fmt(q.preco_com_desconto || 0)}
                                </div>
                                <div style={{ fontSize:11, color:'var(--red)', marginTop:2, fontFamily:'DM Mono,monospace' }}>
                                  − {fmt(q.valor_desconto || 0)} de desconto
                                </div>
                              </div>
                            )}
                            <div style={{ gridColumn: 'span 4', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContext: 'space-between', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                                  Ambientes
                                </div>
                                {editingAmbientesId !== q.id && (
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      setEditingAmbientesId(q.id);
                                      setEditingAmbientes(q.descricao ? q.descricao.split(' | ') : ['']);
                                    }}
                                    style={{ fontSize: 11, color: 'var(--blue)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                  >
                                    ✏ Editar ambientes
                                  </button>
                                )}
                              </div>

                              {editingAmbientesId === q.id ? (
                                <div>
                                  {editingAmbientes.map((amb, idx) => (
                                    <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                                      <input
                                        type="text"
                                        value={amb}
                                        onChange={e => {
                                          const next = [...editingAmbientes];
                                          next[idx] = e.target.value;
                                          setEditingAmbientes(next);
                                        }}
                                        onClick={e => e.stopPropagation()}
                                        style={{
                                          flex: 1, background: 'var(--bg4)',
                                          border: '1px solid var(--blue-border)',
                                          borderRadius: 6, color: 'var(--text)',
                                          fontFamily: 'Inter,sans-serif', fontSize: 12,
                                          padding: '5px 8px', outline: 'none',
                                        }}
                                      />
                                      {editingAmbientes.length > 1 && (
                                        <button
                                          onClick={e => { e.stopPropagation(); setEditingAmbientes(editingAmbientes.filter((_, i) => i !== idx)); }}
                                          style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, padding: '2px 5px' }}
                                        >✕</button>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    onClick={e => { e.stopPropagation(); setEditingAmbientes([...editingAmbientes, '']); }}
                                    style={{ fontSize: 11, color: 'var(--blue)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 0', marginTop: 2 }}
                                  >
                                    ＋ Adicionar ambiente
                                  </button>
                                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                    <button
                                      onClick={async e => {
                                        e.stopPropagation();
                                        const newDescricao = editingAmbientes.filter(a => a.trim()).join(' | ');
                                        await db.updateMarkupQuote(q.id, { descricao: newDescricao });
                                        setQuotes(prev => prev.map(item => item.id === q.id ? { ...item, descricao: newDescricao } : item));
                                        setEditingAmbientesId(null);
                                      }}
                                      style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none', background: 'var(--green)', color: '#fff' }}
                                    >
                                      Salvar ambientes
                                    </button>
                                    <button
                                      onClick={e => { e.stopPropagation(); setEditingAmbientesId(null); }}
                                      style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)' }}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  {(q.descricao ? q.descricao.split(' | ') : []).filter(Boolean).map((amb: string, i: number) => (
                                    <span key={i} style={{
                                      fontSize: 11, padding: '2px 8px', borderRadius: 20,
                                      background: 'var(--bg4)', border: '1px solid var(--border)',
                                      color: 'var(--muted)', fontFamily: 'DM Mono, monospace'
                                    }}>
                                      {amb}
                                    </span>
                                  ))}
                                  {!q.descricao && <span style={{ fontSize: 12, color: 'var(--muted2)' }}>Nenhum ambiente registrado</span>}
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Status:</span>
                            <select
                              value={q.status}
                              onChange={e => handleUpdateStatus(q.id, e.target.value)}
                              style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)', fontFamily: 'Inter,sans-serif', fontSize: 12, padding: '4px 8px', outline: 'none', cursor: 'pointer' }}
                            >
                              <option value="rascunho">Rascunho</option>
                              <option value="enviado">Enviado</option>
                              <option value="aprovado">Aprovado</option>
                              <option value="recusado">Recusado</option>
                            </select>
                            <div style={{ flex: 1 }} />
                            {q.status === 'aprovado' && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  onCreateOrderFromQuote && onCreateOrderFromQuote(q);
                                }}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 6,
                                  padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 500,
                                  cursor: 'pointer',
                                  background: 'rgba(63,185,80,.15)',
                                  border: '1px solid rgba(63,185,80,.3)',
                                  color: 'var(--green)',
                                }}
                              >
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                  <line x1="12" y1="18" x2="12" y2="12"/>
                                  <line x1="9" y1="15" x2="15" y2="15"/>
                                </svg>
                                Gerar O.S. de montagem
                              </button>
                            )}
                            <button
                              onClick={() => handleLoadQuote(q)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none', background: 'var(--blue)', color: '#fff' }}
                            >
                              Carregar na calculadora
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              }
              {!loadingQuotes && quotes.filter(q => (!filterSearch || q.cliente.toLowerCase().includes(filterSearch.toLowerCase())) && (!filterStatus || q.status === filterStatus)).length === 0 && (
                <div style={{ textAlign: 'center', padding: 48, color: 'var(--muted)', fontSize: 13 }}>
                  Nenhum orçamento encontrado.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showSaveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={() => setShowSaveModal(false)}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 10, width: '100%', maxWidth: 440, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.5)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Salvar orçamento</div>
              <button onClick={() => setShowSaveModal(false)} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Nome do cliente *</label>
                <input type="text" value={saveCliente} onChange={e => setSaveCliente(e.target.value)} placeholder="Ex: Paulo Renato da Silva"
                  style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)', fontFamily: 'Inter,sans-serif', fontSize: 13, padding: '9px 12px', outline: 'none' }} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                <label style={{ fontSize:11, fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.05em' }}>
                  Ambientes do projeto
                </label>
                {saveAmbientes.map((amb, idx) => (
                  <div key={idx} style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <input
                      type="text"
                      value={amb}
                      onChange={e => {
                        const next = [...saveAmbientes];
                        next[idx] = e.target.value;
                        setSaveAmbientes(next);
                      }}
                      placeholder={idx === 0 ? 'Ex: Cozinha' : idx === 1 ? 'Ex: Sala de estar' : 'Ex: Quarto'}
                      style={{ flex:1, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text)', fontFamily:'Inter,sans-serif', fontSize:13, padding:'8px 10px', outline:'none' }}
                    />
                    {saveAmbientes.length > 1 && (
                      <button
                        onClick={() => setSaveAmbientes(saveAmbientes.filter((_, i) => i !== idx))}
                        style={{ background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:16, padding:'4px 6px', borderRadius:6 }}
                      >✕</button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setSaveAmbientes([...saveAmbientes, ''])}
                  style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, color:'var(--blue)', background:'transparent', border:'none', cursor:'pointer', padding:'4px 0', width:'fit-content' }}
                >
                  ＋ Adicionar ambiente
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Status inicial</label>
                <select value={saveStatus} onChange={e => setSaveStatus(e.target.value)}
                  style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)', fontFamily: 'Inter,sans-serif', fontSize: 13, padding: '9px 12px', outline: 'none', cursor: 'pointer' }}>
                  <option value="rascunho">Rascunho</option>
                  <option value="enviado">Enviado</option>
                  <option value="aprovado">Aprovado</option>
                  <option value="recusado">Recusado</option>
                </select>
              </div>
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 7, padding: '12px 14px', fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 16 }}>
                <div><div style={{ color: 'var(--muted2)' }}>Custo total</div><div style={{ fontFamily: 'DM Mono,monospace', color: 'var(--text)' }}>R$ {calc.custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div></div>
                <div><div style={{ color: 'var(--muted2)' }}>Preço sugerido</div><div style={{ fontFamily: 'DM Mono,monospace', color: 'var(--cyan)' }}>R$ {calc.precoSug.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div></div>
                <div><div style={{ color: 'var(--muted2)' }}>Margem</div><div style={{ fontFamily: 'DM Mono,monospace', color: 'var(--green)' }}>{margemLiquida.toFixed(1).replace('.', ',')}%</div></div>
              </div>
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowSaveModal(false)} style={{ padding: '8px 16px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)' }}>Cancelar</button>
              <button onClick={handleSaveQuote} style={{ padding: '8px 16px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: 'var(--blue)', color: '#fff' }}>
                Confirmar e salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeLockModal && (
        <div
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }}
          onClick={() => setShowUpgradeLockModal(false)}
        >
          <div
            style={{ background:'var(--bg2)', border:'1px solid var(--border2)', borderRadius:10, width:'100%', maxWidth:400, overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,.5)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:8, background:'var(--blue-dim)', border:'1px solid var(--blue-border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="16" height="16" fill="none" stroke="var(--blue)" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div style={{ fontSize:15, fontWeight:600, color:'var(--text)' }}>Salvar orçamentos</div>
              <button onClick={() => setShowUpgradeLockModal(false)} style={{ marginLeft:'auto', background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:18 }}>✕</button>
            </div>
            <div style={{ padding:'20px' }}>
              <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.7, marginBottom:20 }}>
                Salvar e acessar o histórico de orçamentos está disponível nos planos{' '}
                <strong style={{ color:'var(--blue)' }}>Pro</strong> e{' '}
                <strong style={{ color:'var(--purple)' }}>Business</strong>.
              </p>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setShowUpgradeLockModal(false)} style={{ flex:1, padding:'9px', borderRadius:7, fontSize:13, fontWeight:500, cursor:'pointer', background:'transparent', border:'1px solid var(--border)', color:'var(--muted)' }}>
                  Fechar
                </button>
                <button
                  onClick={() => { setShowUpgradeLockModal(false); onChangeView('UPGRADE'); }}
                  style={{ flex:2, padding:'9px', borderRadius:7, fontSize:13, fontWeight:600, cursor:'pointer', border:'none', background:'var(--blue)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}
                >
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                  Ver planos de upgrade
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Excluir orçamento"
        message="Tem certeza que deseja excluir este orçamento? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        confirmStyle="danger"
        onConfirm={async () => {
          if (confirmDelete) {
            await db.deleteMarkupQuote(confirmDelete);
            setQuotes(prev => prev.filter(q => q.id !== confirmDelete));
            setConfirmDelete(null);
          }
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};
